import { AssessmentCard } from "@app/assessments/components/AssessmentCard";
import { ResourcesSection } from "@app/resources/components/ResourcesSection";
import { lessonAtom } from "@core/grades/atoms";
import { formatDuration } from "@core/shared/utils";
import { Button } from "@design-system/components/Button";
import { trans } from "@mongez/localization";
import {
  BookOpenCheck,
  Clock3,
  FolderOpen,
  Layers3,
  LockKeyhole,
  ShieldCheck,
  Timer,
} from "lucide-react";
import { useLessonResources } from "../hooks";
import { usePurchaseLesson } from "../hooks/lesson-details.hooks";
import { DisplayResourcesButtons } from "./DisplayResourcesButtons";

export function LessonContentTab() {
  const lesson = lessonAtom.value;
  const { groupedResources } = useLessonResources();

  const { purchaseLesson } = usePurchaseLesson();

  if (!lesson.isPaid) {
    if (lesson.paymentStatus === "pending") {
      return (
        <div
          className="mx-auto my-6 max-w-2xl rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-7 text-center dark:border-amber-900 dark:from-amber-950/30 dark:to-slate-950"
          id="lesson-content"
        >
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-amber-400 text-white shadow-lg shadow-amber-200 dark:shadow-none">
            <Clock3 className="h-8 w-8" />
          </span>
          <h3 className="mt-5 text-lg font-black text-slate-950 dark:text-white">
            {trans("grades.paymentUnderReview")}
          </h3>
          <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-600 dark:text-slate-300">
            {trans("grades.paymentUnderReviewMessage")}
          </p>
        </div>
      );
    }

    return (
      <div
        className="mx-auto my-6 max-w-2xl rounded-3xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-sky-50 p-7 text-center dark:border-violet-900 dark:from-violet-950/30 dark:via-slate-950 dark:to-sky-950/20"
        id="lesson-content"
      >
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-200 dark:shadow-none">
          <LockKeyhole className="h-8 w-8" />
        </span>
        <h3 className="mx-auto mt-5 max-w-lg text-lg font-black leading-7 text-slate-950 dark:text-white">
          {trans("grades.purchaseLessonMessage")}
        </h3>
        <Button
          size="lg"
          color="primary"
          className="mt-5 rounded-2xl px-8"
          onClick={purchaseLesson}
        >
          {lesson.isFree ? trans("startLearning") : trans("enrollNow")}
        </Button>
      </div>
    );
  }

  if (lesson.isLocked && lesson.preRequisitesExams?.length) {
    return (
      <div className="space-y-6 py-4 sm:space-y-8" id="lesson-content">
        <div className="flex flex-col gap-4 rounded-3xl border border-orange-200 bg-orange-50 p-5 dark:border-orange-900 dark:bg-orange-950/25 sm:flex-row sm:items-center sm:p-6">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-orange-500 text-white">
            <ShieldCheck className="h-7 w-7" />
          </span>
          <div>
            <h3 className="text-lg font-black text-orange-950 dark:text-orange-100">
              {trans("grades.prerequisitesRequired")}
            </h3>
            <p className="mt-1 text-sm leading-6 text-orange-800 dark:text-orange-200">
              {trans("grades.lessonLockedByPrerequisites")}
            </p>
            <p className="mt-1 text-xs font-bold text-orange-700 dark:text-orange-300">
              {trans("grades.completePrerequisitesFirst")}
            </p>
          </div>
        </div>

        <section>
          <h3 className="mb-4 text-lg font-black text-slate-950 dark:text-white">
            {trans("grades.prerequisitesRequired")}
          </h3>
          <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
            {lesson.preRequisitesExams.map((exam) => (
              <AssessmentCard key={exam.id} assessment={exam} />
            ))}
          </div>
        </section>
      </div>
    );
  }

  const resourcesCount = lesson.resources?.length || 0;
  const resourcesGroupsCount = Object.keys(groupedResources).length;
  const totalDuration =
    lesson.resources?.reduce(
      (total, resource) => total + (resource.duration || 0),
      0,
    ) || 0;

  return (
    <div className="space-y-5 py-3 sm:py-5" id="lesson-content">
      <div className="relative overflow-hidden rounded-[2rem] border border-sky-100 bg-gradient-to-br from-sky-50 via-white to-indigo-50 p-5 dark:border-sky-900/60 dark:from-sky-950/30 dark:via-slate-950 dark:to-indigo-950/20 sm:p-6">
        <div className="absolute -end-12 -top-16 h-40 w-40 rounded-full bg-sky-300/20 blur-3xl" />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-500 text-white shadow-lg shadow-sky-200 dark:shadow-none">
              <FolderOpen className="h-7 w-7" />
            </span>
            <div>
              <h3 className="text-lg font-black text-slate-950 dark:text-white">
                {trans("grades.lessonLearningMaterials")}
              </h3>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
                <BookOpenCheck className="h-4 w-4" />
                {trans("grades.learningMaterialsCount", {
                  count: resourcesCount,
                })}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <MaterialStat
              icon={Layers3}
              label={trans("grades.materialGroupsCount", {
                count: resourcesGroupsCount,
              })}
            />
            {totalDuration > 0 && (
              <MaterialStat
                icon={Timer}
                label={formatDuration(totalDuration)}
                ltr
              />
            )}
            <div className="shrink-0">
              <DisplayResourcesButtons />
            </div>
          </div>
        </div>
      </div>

      <ResourcesSection
        groupedResources={groupedResources}
        className="rounded-[2rem]"
      />
    </div>
  );
}

type MaterialStatProps = {
  icon: React.ComponentType<{ className?: string }>;
  label: React.ReactNode;
  ltr?: boolean;
};

function MaterialStat({ icon: Icon, label, ltr }: MaterialStatProps) {
  return (
    <span className="inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-xl border border-white bg-white/85 px-3 text-sm font-bold text-slate-700 shadow-sm backdrop-blur dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-200">
      <Icon className="h-4 w-4 text-primary" />
      <span dir={ltr ? "ltr" : undefined}>{label}</span>
    </span>
  );
}
